import {
  Box,
  Card,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  HeadingLevel,
  IconPosition,
  MaterialColors,
  SkippedStepTypes,
} from "@/core/types/types";
import DynamicMaterialIcons from "@/core/components/MaterialIcons/MaterialIcons";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { TextVariant } from "@/core/components/Field/Text";

import { FieldProps } from "@/core/components/Field";
import { Fragment, useEffect } from "react";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";

type ListElement = {
  query: string;
  checked: boolean;
  type?: string | undefined;
  shown: boolean;
};

interface OptionalStepStatusLinkRequiredVariables {
  [key: string]: string | boolean | number | undefined;
}

interface PreviousAnswersResult {
  result: OptionalStepStatusLinkRequiredVariables;
}

type GutterlessListProps = {
  listElements: ListElement[];
};

const ADDITIONAL_PROPERTIES: string[] = ["legalCasePublicId"];

const CustomDivider = () => {
  const theme = useTheme();
  return <Divider sx={{ borderColor: theme.palette.secondary.light }} />;
};

const StyledListItemUncompleted = ({
  text,
  type,
}: {
  text: string;
  type?: string;
}) => {
  const RightArrow = DynamicMaterialIcons["KeyboardArrowRight"];
  return type && type in SkippedStepTypes ? (
    <ListItemButton component="button" sx={{ width: "100%" }}>
      <ListItem
        key={text}
        disableGutters
        onClick={(e) => {
          e.stopPropagation();
          // TODO: implement step submission
        }}
      >
        <Link color="secondary" underline="hover">
          <TextVariant
            text={text}
            variant={HeadingLevel.Body2}
            iconName="Cancel"
            iconColor={MaterialColors.Error}
            iconPosition={IconPosition.IconBefore}
          />
        </Link>
        <RightArrow />
      </ListItem>
    </ListItemButton>
  ) : (
    <ListItem key={text} disableGutters sx={{ padding: 2 }}>
      <TextVariant
        text={text}
        variant={HeadingLevel.Body2}
        iconName="Cancel"
        iconColor={MaterialColors.Error}
        iconPosition={IconPosition.IconBefore}
      />
    </ListItem>
  );
};

const StyledListItem = (props: {
  checked: boolean;
  type?: string;
  text: string;
}) => {
  const {
    checked,
    type,
    text,
  }: { checked: boolean; type?: string | undefined; text: string } = props;
  return checked ? (
    <StyledListItemCompleted text={text} />
  ) : (
    <StyledListItemUncompleted text={text} type={type} />
  );
};

const StyledListItemCompleted = ({ text }: { text: string }) => {
  const theme = useTheme();

  return (
    <ListItem key={text} disableGutters sx={{ padding: 2 }}>
      <TextVariant
        text={text}
        variant={HeadingLevel.Body2}
        iconName="CheckCircle"
        iconCustomColor={theme.palette.success.main}
        iconPosition={IconPosition.IconBefore}
      />
    </ListItem>
  );
};

function GutterlessList({ listElements }: Readonly<GutterlessListProps>) {
  return (
    <List sx={{ "& li": { justifyContent: "space-between" }, pb: 0 }}>
      {listElements?.map((element, index) => {
        let listItemText = t(
          `showNextStepsForm.${element.query}.${
            element.checked ? "completed" : "missing"
          }`
        );

        return (
          element.shown && (
            <Fragment key={index}>
              <StyledListItem
                checked={element.checked}
                type={element.type}
                text={listItemText}
              />
              {index !== listElements.length - 1 && <CustomDivider />}
            </Fragment>
          )
        );
      })}
    </List>
  );
}

const OptionalStepStatusLink = ({ field }: FieldProps) => {
  const {
    properties: { requiredInputVariables },
  } = field;

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const getRequiredInputVariablesProperties = (): string => {
    const allRequiredInputVariables: string[] = [];
    const requiredInputVariablesArray: string[] =
      requiredInputVariables.split(",");
    for (let variable of requiredInputVariablesArray) {
      if (ADDITIONAL_PROPERTIES.includes(variable)) {
        allRequiredInputVariables.push(variable);
      } else {
        allRequiredInputVariables.push(`${variable}Checked`);
        allRequiredInputVariables.push(`${variable}Shown`);
      }
    }
    return allRequiredInputVariables.join(",");
  };

  const { result }: PreviousAnswersResult = useGetPreviousAnswers(
    getRequiredInputVariablesProperties()
  );

  const getMissingStepType = (key: string): string | undefined => {
    switch (
      key
      // TODO: set the step type so that we can submit it when the user clicks
    ) {
    }
    return undefined;
  };

  const getMissingSteps = (): ListElement[] => {
    const missingSteps: ListElement[] = [];

    for (const key of requiredInputVariables.split(",")) {
      if (
        !(result.hasOwnProperty(key) && ADDITIONAL_PROPERTIES.includes(key))
      ) {
        missingSteps.push({
          query: key,
          checked: result[`${key}Checked`] === true,
          shown: result[`${key}Shown`] === true,
          type: getMissingStepType(key),
        });
      }
    }
    return missingSteps;
  };

  useEffect(() => {
    getMissingSteps();
  }, []);

  return (
    <Card sx={{ boxShadow: "none" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        m={2}
      >
        <Typography variant={HeadingLevel.Body2}>
          {t("showNextStepsForm.title")}
        </Typography>
        <Typography variant={HeadingLevel.Subtitle1}>
          {result?.legalCasePublicId}
        </Typography>
      </Box>
      <CustomDivider />
      <GutterlessList listElements={getMissingSteps()} />
    </Card>
  );
};

export default OptionalStepStatusLink;
